import {
  Badge, Box, Flex, HStack, Icon, IconButton, Text, Tooltip, useColorModeValue,
} from "@chakra-ui/react";
import { DeleteIcon, EditIcon, RepeatIcon, WarningTwoIcon } from "@chakra-ui/icons";
import { Mail } from "lucide-react";
import type { WhitelistEntry } from "../../../../types/whitelist";
import { formatSp } from "../../../../lib/datetime";
import { UserAvatar } from "../../../../components/UserAvatar";

interface WhitelistEntryRowProps {
  entry: WhitelistEntry;
  isRemoving: boolean;
  isResending: boolean;
  onEdit: (entry: WhitelistEntry) => void;
  onRemove: (email: string) => void;
  onResendWelcome: (entry: WhitelistEntry) => void;
  onSetEmail: (entry: WhitelistEntry) => void;
}

export const WhitelistEntryRow = ({
  entry, isRemoving, isResending, onEdit, onRemove, onResendWelcome, onSetEmail,
}: WhitelistEntryRowProps) => {
  const rowHover   = useColorModeValue("gray.50", "whiteAlpha.50");
  const labelColor = useColorModeValue("gray.500", "gray.400");

  return (
    <Flex
      align="center" px={5} py={3} gap={3}
      _hover={{ bg: rowHover }} transition="background 0.12s"
    >
      <UserAvatar
        size="sm"
        name={entry.candidate_full_name ?? entry.email}
        email={entry.email}
        flexShrink={0}
      />

      <Box flex={1} minW={0}>
        {entry.candidate_full_name && (
          <Text fontSize="sm" fontWeight={600} noOfLines={1} lineHeight={1.3}>
            {entry.candidate_full_name}
          </Text>
        )}
        {entry.email_missing ? (
          <HStack spacing={1} mt={entry.candidate_full_name ? 0 : undefined}>
            <WarningTwoIcon color="orange.400" boxSize="10px" />
            <Text fontSize="xs" color="orange.400" fontWeight={500} fontStyle="italic">
              E-mail não disponível pelo Gupy
            </Text>
          </HStack>
        ) : (
          <Text
            fontSize={entry.candidate_full_name ? "xs" : "sm"}
            fontWeight={entry.candidate_full_name ? 400 : 500}
            color={entry.candidate_full_name ? labelColor : undefined}
            noOfLines={1}
          >
            {entry.email}
          </Text>
        )}
        <Text fontSize="10px" color={entry.responsible_manager ? "blue.400" : "orange.400"} mt={0.5} noOfLines={1} fontWeight={500}>
          {entry.responsible_manager ? `Gestor: ${entry.responsible_manager}` : "Gestor não informado"}
        </Text>
        <Text fontSize="10px" color={labelColor} mt={0.5}>
          Adicionado em {formatSp(entry.created_at, "dd/MM/yyyy")}
        </Text>
      </Box>

      <HStack spacing={1} flexShrink={0}>
        {entry.email_missing ? (
          <Badge colorScheme="orange" borderRadius="full" fontSize="10px" px={2} py={0.5} fontWeight={600}>
            E-mail pendente
          </Badge>
        ) : (
          <Badge colorScheme="green" borderRadius="full" fontSize="10px" px={2} py={0.5} fontWeight={600}>
            Autorizado
          </Badge>
        )}
        {!entry.email_missing && entry.welcome_email_status === "sent" && (
          <Badge colorScheme="green" variant="subtle" borderRadius="full" fontSize="10px" px={2} py={0.5} fontWeight={600}>
            E-mail enviado
          </Badge>
        )}
        {!entry.email_missing && entry.welcome_email_status === "failed" && (
          <Badge colorScheme="red" borderRadius="full" fontSize="10px" px={2} py={0.5} fontWeight={600}>
            E-mail falhou
          </Badge>
        )}
        {!entry.email_missing && entry.welcome_email_status === "pending" && (
          <Badge colorScheme="yellow" borderRadius="full" fontSize="10px" px={2} py={0.5} fontWeight={600}>
            Enviando...
          </Badge>
        )}
        {!entry.responsible_manager && (
          <Badge colorScheme="orange" variant="subtle" borderRadius="full" fontSize="10px" px={2} py={0.5} fontWeight={600}>
            Sem gestor
          </Badge>
        )}
        {entry.is_pj && (
          <Badge colorScheme="orange" borderRadius="full" fontSize="10px" px={2} py={0.5} fontWeight={600}>PJ</Badge>
        )}
        {entry.is_tech && (
          <Badge colorScheme="blue" borderRadius="full" fontSize="10px" px={2} py={0.5} fontWeight={600}>Tech</Badge>
        )}
      </HStack>

      <HStack spacing={0} flexShrink={0}>
        {entry.email_missing ? (
          <Tooltip label="Informar e-mail real" placement="left" hasArrow fontSize="xs">
            <IconButton
              aria-label="Informar e-mail"
              icon={<Icon as={Mail} boxSize="14px" />}
              colorScheme="orange" variant="ghost" size="sm" borderRadius="lg"
              onClick={() => onSetEmail(entry)}
            />
          </Tooltip>
        ) : (
          <>
            {entry.welcome_email_status === "failed" && (
              <Tooltip label="Reenviar e-mail de boas-vindas" placement="left" hasArrow fontSize="xs">
                <IconButton
                  aria-label="Reenviar e-mail" icon={<RepeatIcon />}
                  colorScheme="red" variant="ghost" size="sm" borderRadius="lg"
                  isLoading={isResending} onClick={() => onResendWelcome(entry)}
                />
              </Tooltip>
            )}
            <Tooltip label="Editar classificação" placement="left" hasArrow fontSize="xs">
              <IconButton
                aria-label="Editar candidato" icon={<EditIcon />}
                variant="ghost" size="sm" borderRadius="lg"
                onClick={() => onEdit(entry)}
              />
            </Tooltip>
          </>
        )}
        <Tooltip label="Remover acesso" placement="left" hasArrow fontSize="xs">
          <IconButton
            aria-label="Remover e-mail" icon={<DeleteIcon />}
            colorScheme="red" variant="ghost" size="sm" borderRadius="lg"
            isLoading={isRemoving} onClick={() => onRemove(entry.email)}
          />
        </Tooltip>
      </HStack>
    </Flex>
  );
};
